/**
 * Thin typed web API client for a candidacy's stored reports (`web/v2`):
 * four kinds in two groups — the Recruiter and Client Assessments, the
 * Official and Backchannel References — each stored as Word, PDF and plain
 * text (epic `2026-10-06-search-candidates-reports`, sub-task 04; four kinds
 * since epic `2026-10-22-candidate-reports-four-kinds`, whose D1 keeps the
 * stored ids `assessment` and `references` for the Recruiter and Official
 * reports).
 *
 * Three routes, all under one candidacy of one search:
 *
 *   - `GET  …/reports` — the index: per kind, whether a copy is stored,
 *     being built, absent or deleted, and how many versions it has;
 *   - `GET  …/reports/{kind}/download?format=&disposition=` — a SIGNED S3 URL
 *     (five minutes) plus the filename; asked for PER CLICK and never cached,
 *     so a slow click cannot land on an expired link. With `liveCheck: true`
 *     (a user's Download or Open PDF click) the API reads Thrive first (#874)
 *     and answers 409 — `CandidateReportRebuildingError` here — while a stale
 *     report is rebuilt; without it (the preview) it serves what is stored;
 *   - `POST …/reports/{kind}/refresh` — enqueue a rebuild from Thrive; answers
 *     202 with the index, in which the kind now reads `building`.
 *
 * Separate from `searchReport.client.ts` because it is a different API module
 * (`app/web/v2/candidate_reports/`) with its own lifecycle; the candidates
 * table's two presence filters for these reports ride that client, since they
 * are query parameters of ITS endpoint.
 *
 * Cookie auth rides `makeAuthedFetch` like every other web client — the panel
 * POLLS while a report builds, which is exactly where a bare `fetch` would die
 * on an expired access-token cookie.
 */

import type { SearchCandidateRow, components, operations } from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

// Derived from the generated contract rather than restated, so a shape the
// API changes breaks `tsc` here instead of shipping a button that 422s. Named
// locally because `@nu-assist/contracts` exports no aliases for this module
// yet; moving them there is the integrator's call (it owns the package).
type DownloadOperation =
  operations["get_candidate_report_download_api_web_v2_searches__search_id__candidacies__candidacy_id__reports__kind__download_get"]["parameters"];

export type CandidateReportState = components["schemas"]["CandidateReportState"];
export type CandidateReportsResponse = components["schemas"]["CandidateReportsResponse"];
export type CandidateReportDownload = components["schemas"]["CandidateReportDownload"];
export type CandidateReportKind = DownloadOperation["path"]["kind"];
export type CandidateReportFormat = DownloadOperation["query"]["format"];
export type CandidateReportStatus = CandidateReportState["status"];
export type CandidateReportDisposition = NonNullable<DownloadOperation["query"]["disposition"]>;

/** The four kinds, in the API's order (`REPORT_KINDS`): assessments, then references. */
export const CANDIDATE_REPORT_KINDS: readonly CandidateReportKind[] = [
  "assessment",
  "client_assessment",
  "references",
  "backchannel_references",
];

/** The report fields of a Candidates row (§3), as the helpers below read them. */
export type CandidateReportFlags = Pick<
  SearchCandidateRow,
  | "has_recruiter_assessment"
  | "assessment_by"
  | "assessment_on"
  | "assessment_updated_at"
  | "has_client_assessment"
  | "client_assessment_updated_at"
  | "references_count"
  | "references_updated_at"
  | "backchannel_references_count"
  | "backchannel_references_updated_at"
>;

export type CandidateReportGroupId = "assessments" | "references";

export interface CandidateReportGroup {
  id: CandidateReportGroupId;
  /** The panel's caption and the Reports cell's button. */
  label: string;
  kinds: readonly CandidateReportKind[];
}

/** The two groups the panel and the Reports cell show, in that order. */
export const REPORT_GROUPS: readonly CandidateReportGroup[] = [
  { id: "assessments", label: "Assessments", kinds: ["assessment", "client_assessment"] },
  { id: "references", label: "References", kinds: ["references", "backchannel_references"] },
];

/** The report's name: the downloads row, the menu sections, the filenames. */
export const REPORT_KIND_LABEL: Record<CandidateReportKind, string> = {
  assessment: "Recruiter Assessment",
  client_assessment: "Client Assessment",
  references: "Official References",
  backchannel_references: "Backchannel References",
};

/** The segment's word inside its group's caption. */
export const REPORT_KIND_SHORT_LABEL: Record<CandidateReportKind, string> = {
  assessment: "Recruiter",
  client_assessment: "Client",
  references: "Official",
  backchannel_references: "Backchannel",
};

/** Why a kind is not offered, when the row's projection says Thrive lacks it. */
export const REPORT_KIND_MISSING_REASON: Record<CandidateReportKind, string> = {
  assessment: "Thrive has no recruiter assessment on this candidacy.",
  client_assessment: "Thrive has no client assessment on this candidacy.",
  references: "Thrive has no official references for this candidate.",
  backchannel_references: "Thrive has no backchannel references for this candidate.",
};

/** Does the row's projection say Thrive has this kind of report? */
export function rowHasReport(
  row: Partial<CandidateReportFlags>,
  kind: CandidateReportKind
): boolean {
  switch (kind) {
    case "assessment":
      return row.has_recruiter_assessment === true;
    case "client_assessment":
      return row.has_client_assessment === true;
    case "references":
      return (row.references_count ?? 0) > 0;
    case "backchannel_references":
      return (row.backchannel_references_count ?? 0) > 0;
  }
}

/** The projection's "Thrive last changed this" stamp for one kind. */
export function rowSourceUpdatedAt(
  row: Partial<CandidateReportFlags>,
  kind: CandidateReportKind
): string | null {
  switch (kind) {
    case "assessment":
      return row.assessment_updated_at ?? null;
    case "client_assessment":
      return row.client_assessment_updated_at ?? null;
    case "references":
      return row.references_updated_at ?? null;
    case "backchannel_references":
      return row.backchannel_references_updated_at ?? null;
  }
}

/** How many referees the row counts for a references kind; null for an assessment. */
export function rowRefereeCount(
  row: Partial<CandidateReportFlags>,
  kind: CandidateReportKind
): number | null {
  switch (kind) {
    case "references":
      return row.references_count ?? 0;
    case "backchannel_references":
      return row.backchannel_references_count ?? 0;
    case "assessment":
    case "client_assessment":
      return null;
  }
}

/**
 * The kinds a candidate offers, in `CANDIDATE_REPORT_KINDS` order: the row's
 * projection when the row is on the page, else — a deep link — the index's
 * `present` / `building` entries.
 */
export function availableKinds(
  row: Partial<CandidateReportFlags> | null,
  reports: readonly CandidateReportState[] | undefined
): CandidateReportKind[] {
  if (row) return CANDIDATE_REPORT_KINDS.filter((kind) => rowHasReport(row, kind));
  return CANDIDATE_REPORT_KINDS.filter((kind) => {
    const status = reports?.find((r) => r.kind === kind)?.status;
    return status === "present" || status === "building";
  });
}

/**
 * Can this kind hand out a file, as far as the index has said? Stored formats
 * decide, whatever the status (a rebuild keeps the previous build's files
 * downloadable). NOT ANSWERED YET, or the read failed, counts as yes: the
 * Reports cell offers the kind, and a download of a copy that is not there
 * answers 404 and lands in the toast.
 */
export function reportMayServe(state: CandidateReportState | undefined): boolean {
  return state === undefined || state.formats.length > 0;
}

/**
 * The kind a picker selects first — ONE rule for the candidate panel and the
 * Reports cell (/pr-review #1092 iter 1): the first of `kinds` (in their
 * order) that can serve a file, else the first of `kinds`, null when `kinds`
 * is empty. The fallback keeps the panel on a report it can describe ("not
 * stored yet", "Preparing…") when none can be downloaded.
 */
export function defaultReportKind(
  kinds: readonly CandidateReportKind[],
  stateOf: (kind: CandidateReportKind) => CandidateReportState | undefined
): CandidateReportKind | null {
  return kinds.find((kind) => reportMayServe(stateOf(kind))) ?? kinds[0] ?? null;
}

/**
 * The format a picker selects first: PDF when it is offered, else the first
 * offered one (in `offered`'s order), null when none is.
 */
export function defaultReportFormat(
  offered: readonly CandidateReportFormat[]
): CandidateReportFormat | null {
  return offered.includes("pdf") ? "pdf" : (offered[0] ?? null);
}

/** Minimal `fetch`-shaped function — `makeAuthedFetch(...)` by default. */
type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface CandidateReportsClientConfig {
  /** API origin (e.g. `import.meta.env.VITE_API_URL`). */
  apiBase: string;
  /** Defaults to `makeAuthedFetch({ apiBase, onSessionExpired })`. */
  fetchImpl?: FetchLike;
}

export class CandidateReportsApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message?: string
  ) {
    super(message ?? `Candidate reports request failed (${status}) for ${url}`);
    this.name = "CandidateReportsApiError";
    Object.setPrototypeOf(this, CandidateReportsApiError.prototype);
  }
}

/** A download of a kind that is not stored, or a refresh of a candidacy the
 *  index has never seen — asking again will not change the answer. */
export class CandidateReportNotFoundError extends CandidateReportsApiError {
  constructor(url: string) {
    super(404, url, "That report is not stored.");
    this.name = "CandidateReportNotFoundError";
    Object.setPrototypeOf(this, CandidateReportNotFoundError.prototype);
  }
}

/**
 * The download's 409 (#874): Thrive holds a newer source than the stored
 * report (or the report was rendered by an older layout), and a rebuild is
 * queued. `body` is the candidacy's reports with that kind `building` — the
 * same shape a Refresh answers — and `retryAfterSeconds` the poll interval.
 */
export class CandidateReportRebuildingError extends CandidateReportsApiError {
  constructor(
    url: string,
    public readonly body: CandidateReportsResponse,
    public readonly retryAfterSeconds: number
  ) {
    super(409, url, "That report is being updated from Thrive.");
    this.name = "CandidateReportRebuildingError";
    Object.setPrototypeOf(this, CandidateReportRebuildingError.prototype);
  }
}

/** `Retry-After` in seconds, else the body's interval, else 3 (the API's). */
function retryAfterSeconds(res: Response, body: CandidateReportsResponse): number {
  const header = Number(res.headers?.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) return header;
  return body.refresh_after_seconds > 0 ? body.refresh_after_seconds : 3;
}

export interface CandidateReportDownloadOptions {
  /**
   * `true` asks the API to read Thrive first and answer 409 while a stale
   * report is rebuilt — a user's Download or Open PDF click (#874). Default
   * `false` (the API's default; the parameter is then not sent): what is
   * stored — the preview, the previous build while a rebuild runs, the build
   * a rebuild just wrote.
   */
  liveCheck?: boolean;
}

export function createCandidateReportsClient(cfg: CandidateReportsClientConfig) {
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  function reportsUrl(searchId: string, candidacyId: string): string {
    return (
      `${cfg.apiBase}/api/web/v2/searches/${encodeURIComponent(searchId)}` +
      `/candidacies/${encodeURIComponent(candidacyId)}/reports`
    );
  }

  async function send<T>(url: string, method: "GET" | "POST"): Promise<T> {
    const res = await authedFetch(url, { method, credentials: "include" });
    if (res.status === 404) throw new CandidateReportNotFoundError(url);
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as CandidateReportsResponse | null;
      if (body && Array.isArray(body.reports)) {
        throw new CandidateReportRebuildingError(url, body, retryAfterSeconds(res, body));
      }
    }
    if (!res.ok) throw new CandidateReportsApiError(res.status, url);
    return (await res.json()) as T;
  }

  return {
    /** GET …/candidacies/{id}/reports — the four kinds' stored state. */
    getReports(searchId: string, candidacyId: string): Promise<CandidateReportsResponse> {
      return send(reportsUrl(searchId, candidacyId), "GET");
    },

    /**
     * GET …/reports/{kind}/download — a fresh signed URL, one per call.
     * With `liveCheck: true`, throws `CandidateReportRebuildingError` (409)
     * while a stale report is rebuilt from Thrive.
     */
    getDownload(
      searchId: string,
      candidacyId: string,
      kind: CandidateReportKind,
      format: CandidateReportFormat,
      disposition: CandidateReportDisposition = "attachment",
      options: CandidateReportDownloadOptions = {}
    ): Promise<CandidateReportDownload> {
      const params = new URLSearchParams({ format, disposition });
      if (options.liveCheck === true) params.set("live_check", "true");
      return send(
        `${reportsUrl(searchId, candidacyId)}/${kind}/download?${params.toString()}`,
        "GET"
      );
    },

    /** POST …/reports/{kind}/refresh — 202 with the index, the kind `building`. */
    refresh(
      searchId: string,
      candidacyId: string,
      kind: CandidateReportKind
    ): Promise<CandidateReportsResponse> {
      return send(`${reportsUrl(searchId, candidacyId)}/${kind}/refresh`, "POST");
    },
  };
}

export type CandidateReportsClient = ReturnType<typeof createCandidateReportsClient>;
